import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductCardSelections } from "@MasterBluePrint/Overrides/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@MasterBluePrint/Overrides/Widgets/ProductList/ProductListActions";
import ProductListHazmatIcon from "@MasterBluePrint/Overrides/Widgets/ProductList/ProductListHazmatIcon";
import React, { FC } from "react";
import { css } from "styled-components";


type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    productImageWrapper?: InjectableCss;
    partNumsAndTitleWrapper?: InjectableCss;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    availabilityStylesCustom?: InjectableCss;
    leadTimeContainer?: InjectableCss;
    leadTimeLabel?: InjectableCss;
    leadTimeValue?: InjectableCss;
    tooltipStyles?: InjectableCss;
    totalAvilableFutureContainer?: InjectableCss;
    totalAvilableFutureLabel?: InjectableCss;
    totalAvilableFutureValue?: InjectableCss;
    minQtyLabel?: InjectableCss;
    minQtyValue?: InjectableCss;
    nonCancellableNonReturnableContainer?: InjectableCss;
    nonCancellableNonReturnableText?: InjectableCss;
    nonCancellableNonReturnableAnchor?: InjectableCss;
    parentProductInfomationLabel?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 1.875rem 0;
            min-width: 20rem /* prevent overlap with second column */

            @media screen and (max-width: 1399px) {
                padding: 1.25rem 0 .31250625rem 0;
            }
            @media screen and (max-width: 1280px) {
                padding: 1rem 30px 1.5rem 0;
            }
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 12, 3],
        css: css`
            padding-right: 1.25rem;
            flex-direction: column;

            @media screen and (max-width: 1399px) {
                flex-basis:100%;
                max-width: 100%;
            }            
            @media screen and (max-width: 1280px) {
                display:flex;
                flex-flow:row wrap;
                padding-right: 0;
            }
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: .31250625rem;
            font-size: .9em;
            opacity: .5;           
            @media screen and (max-width: 1280px) {
                display:block;
                flex:1 100%;
            }
            
        `,
    },
    productImageWrapper: {
        css: css`      
            @media screen and (max-width: 1280px) {
                max-width:7rem;
            }      
            @media screen and (max-width: 430px) {
                max-width:6rem;
            }
        `,
    },
    partNumsAndTitleWrapper: {
        css: css`
            flex:1; 
            @media screen and (max-width: 1280px) {
                padding-top: 5px;
                padding-left:1rem;
                > div > div {margin:0;}
                > div > div > div {
                    padding:0 0 5px;
                }
            }
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 12, 9],
        css: css`
            @media screen and (max-width: 1399px) {
                flex-basis: 100%;
                max-width: 100%;
            }
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            @media screen and (max-width: 1399px) {
                display: block;
                flex-basis: 100%;
                max-width: 100%;
            }
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            @media screen and (max-width: 1399px) {
                flex-basis:100%;
                max-width: 100%;
                padding-top: .125rem;

                .plp-badge div[class*='FlexItem'], 
                .plp-badge div[class^='FlexItem'] {
                    width: auto;
                    max-width: initial;
                    flex-basis: auto;
                    padding: 10px 0 5px;
                }
            }
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
    leadTimeContainer: {
        css: css`
            font-weight: 600;
            font-size: .875rem !important;
            margin-bottom: .3em;
            font-family: 'Lato', sans-serif;
            display: inline-block;
            margin-top: 0;
        `,
    },
    leadTimeLabel: {
        css: css`
            margin-right: .5rem;    
            display: inline-block;
            font-size: .875rem !important;
            font-family: 'Roboto', sans-serif;
            color: #002554;
            font-weight: bold;
            letter-spacing: .028rem;
        `,
    },
    leadTimeValue: {
        css: css`
            color: #333F48;
            font-family: 'Lato', sans-serif;
            font-weight: 400;
            display: inline-block;
            font-size: .875rem !important;
            letter-spacing: 0;
            line-height: 1.25rem;
            span {
                margin-left: 1rem;
            }
        `,
    },
    tooltipStyles: {
        css: css`
        padding:.3rem .5rem;
        width: auto;
        color: #fff;
        margin: 0 0 1rem;
    `,
    },
    totalAvilableFutureContainer: {
        css: css`
            font-weight: 600;
            font-size: .875rem;
            margin-bottom: .3em;
        `,
    },
    totalAvilableFutureLabel: {
        css: css`
            margin-right: .5rem;
            font-family: 'Roboto', sans-serif;
            color: #002554;
            font-weight: bold;
            letter-spacing: .028rem;
            display: inline-block;
            font-size: .875rem !important;
        `,
    },
    totalAvilableFutureValue: {
        css: css`
            color: #333F48;
            font-family: 'Lato', sans-serif;
            font-weight: 400;
            display: inline-block;
            font-size: .875rem !important;
        `,
    },
    minQtyLabel: {
        css: css`
        margin-right: .31250625rem;
        font-style: italic;
        margin-left: .625rem;
        margin-bottom: .6rem;
        position: relative;
        display: inline-block;
        color: #333F48;
        font-family: 'Lato', sans-serif;
        font-size: .875rem;
        letter-spacing: 0;
        line-height: 1.5rem;
        `,
    },
    minQtyValue: {
        css: css`
        color: #333F48;
        font-family: 'Lato', sans-serif;
        font-weight: 400;
        font-style: italic;
        font-size: .875rem;
        `,
    },
    nonCancellableNonReturnableContainer: {
        css: css`
            font-weight: 600;
            font-size: .85rem;
            margin-bottom: .2em;
            text-align: center;
            color: #333F48;
            padding: 0 .938rem .938rem .938rem;
            position: relative;
            top: -1.375rem;
        `,
    },
    nonCancellableNonReturnableText: {
        css: css`
        color: #333F48;
        font-family: 'Lato', sans-serif;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: .003rem
        line-height: 1.1875rem;
        text-align: center;
        font-weight: normal;
        `,
    },
    nonCancellableNonReturnableAnchor: {
        css: css`
        color: #333F48;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: .003rem
        line-height: 1.1875rem;
        text-align: center;
        font-family: 'Lato', sans-serif;
        font-weight: normal;
        &:hover {
            text-decoration: underline !important;
            color: #333F48 !important;
        }
        span {
            color: #333F48 !important;

            &:hover {
                text-decoration: underline !important;
                color: #333F48 !important;
            }
        }

        `,
    },
    parentProductInfomationLabel: {
        css: css`
            margin-right: .5rem;    
            display: block;
            font-size: .875rem !important;
            color: #002554;
            font-weight: 600;
        `,
    },
};

const styles = productCardStyles;



const ProductListProductCard: FC<Props> = props => {
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <StyledWrapper {...styles.productImageWrapper}>
                    <ProductListProductImage
                        showImage={props.showImage}
                        showCompare={props.showCompare}
                        showImageBadge={props.showImageBadges}
                        showTextBadge={props.showTextBadges}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.partNumsAndTitleWrapper}>
                    <ProductListProductInformation
                        showBrand={true}
                        showTitle={true}
                        showPartNumbers={props.showPartNumbers}
                    />
                </StyledWrapper>  
            </GridItem>
            <GridItem {...styles.rightColumnGridItem} >
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem} className="grid-item">
                        <ProductListProductInformation
                            showAvailability={props.showAvailability}
                            showAttributes={false}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                        {!props.product.isVariantParent ?
                            (
                                <>
                                    {props.product.properties.totalAvailableFuture &&
                                        (
                                            <StyledWrapper {...styles.totalAvilableFutureContainer}>
                                                <Typography {...styles.totalAvilableFutureLabel}>
                                                    {siteMessage("avilableFetureLabel") || translate("Total Avilable Future: ")}
                                                </Typography>
                                                <Typography {...styles.totalAvilableFutureValue}>
                                                    {props.product.properties.totalAvailableFuture}
                                                </Typography>
                                            </StyledWrapper>
                                        )}

                                    {props.product.properties.averageLeadTime &&
                                        (
                                            <StyledWrapper {...styles.leadTimeContainer}>
                                                <Typography {...styles.leadTimeLabel}>
                                                    {siteMessage("leadtimelabel") || translate("Manufacturer Lead Time:")}
                                                </Typography>
                                                <Typography {...styles.leadTimeValue}>
                                                    {props.product.properties.averageLeadTime} {translate("Days")}
                                                </Typography>
                                            </StyledWrapper>
                                        )}

                                </>

                            ) : (<>
                                <StyledWrapper>
                                    <Typography {...styles.parentProductInfomationLabel}>
                                        {siteMessage("ParentProduct_PLP_Availability_InfoText")}
                                    </Typography>
                                </StyledWrapper></>)}
                    </GridItem>
                    <GridItem {...styles.actionsGridItem} className="grid-mob-item p-relative">
                        <ProductListActions
                            product={props.product}
                            showPrice={props.showPrice}
                            showAddToList={false}
                            displayQtyBreakPricingInTable={props.displayQtyBreakPricingInTable}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                    </GridItem>
                    <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem>
                    <GridItem {...styles.infoGridItem} className="last-grid-item">
                        <ProductListHazmatIcon
                            showShippingRestrictionIcon={props.showShippingRestrictionIcon}
                            showHazardousGoodIcon={props.showHazardousGoodIcon}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withProduct(ProductListProductCard);
